
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface CheckoutButtonProps {
  totalPoints: number;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  totalPoints,
  className = "w-full bg-brand-red hover:bg-brand-red/90 h-12 text-lg",
  onClick,
  disabled
}) => {
  const { t } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const { user, userProfile } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Verificar se estamos na página de checkout, não mostrar o botão nesse caso
  const isCheckoutPage = location.pathname === "/checkout";
  
  // Se estamos em uma página diferente da home, não mostrar o botão
  if (isCheckoutPage) {
    return null;
  }

  const handleClick = async () => {
    
    if (onClick) {
      onClick();
      return;
    }

    setProcessing(true);
    try {
      
      // Verificar se o usuário está logado
      if (!user) {
        toast.error(t('checkout.loginRequired'));
        navigate("/auth", { state: { redirect: "/checkout" } });
        return;
      }
      
      // Verificar se o carrinho não está vazio
      if (cart.length === 0) {
        toast.error(t('cart.empty'));
        return;
      }

      // Verificar se tem conta conectada
      if (!userProfile?.connectedAccountId) {
        toast.error(t('checkout.connectAccountRequired'));
        return;
      }

      // Verificar se tem storepoints suficientes
      const userPoints = userProfile.points || 0;
      
      if (userPoints < totalPoints) {
        toast.error(t('checkout.insufficientPoints'));
        return;
      }
      
      navigate("/checkout");
    } catch (error) {
      // Checkout error
      toast.error(t('checkout.processingError'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={processing || disabled}
      className={className}
    >
      {processing ? (
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
      ) : (
        <ShoppingCart className="mr-2" />
      )}
      {processing ? t('common.loading') : t('checkout.finalizePurchase')}
    </Button>
  );
};

export default CheckoutButton;
